import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Alert, Spinner } from "react-bootstrap"; // Import Alert and Spinner for better UX
import { fetchTeacherTimetable } from "./redux/timetableSlice"; // Ensure this is the correct action
import TeacherTimeTableView from "./components/TeacherTimeTableView"; // Assuming this component renders the table
import { fetchTeachers } from "../teachers/redux/teacherSlice";
import { fetchPeriods } from "../periods/redux/periodSlice";
import userStore from "../../../zustand/userStore";
import { useLocation, useNavigate } from "react-router-dom";
import { teacherTimetableSteps } from "../../../tour/timetableTourSteps";
import Joyride from "react-joyride";
import { handleJoyRideRunning } from "../subjects/redux/subjectSlice";
import { createPortal } from "react-dom";
import { startTour, stopTour } from "../../../redux/globalJoyrideSlice";
import GlobalJoyrideCloseButton from "../../../components/GlobalJoyrideCloseButton";

const ViewTeacherTimeTableManagement = () => {
  const dispatch = useDispatch();

  const {
    teachers,
    loading: teachersLoading,
    error: teachersError,
  } = useSelector((state) => state.teachers);
  const {
    teacherTimetable,
    loading: timetableLoading,
    error: timetableError,
  } = useSelector((state) => state.timetable);
  const {
    periods,
    loading: periodsLoading,
    error: periodsError,
  } = useSelector((state) => state.periods);
  const masterData = useSelector((state) => state.masterData);
  const schoolId = masterData?.school_id; // Using optional chaining for safety

  // Get user_id from Zustand store
  const userData = userStore((state) => state.user);
  const userId = userData?.id; // Assuming the user object has an 'id' property
  const roles = userData?.roles;
  const { run, activeTour } = useSelector((state) => state.globalJoyride);

  const location = useLocation();
  const navigate = useNavigate();

  const [selectedTeacher, setSelectedTeacher] = useState(""); // Initialize with empty string for default option
  useEffect(() => {
    if (!location.state?.startTeacherTour) return;
    const waitForDom = setInterval(() => {
      const el = document.querySelector(".teacher-select-step"); // FIRST STEP TARGET CLASS
      if (el) {
        clearInterval(waitForDom);

        dispatch(
          startTour({
            name: "teacherTimetable",
            steps: teacherTimetableSteps(),
          }),
        );
        navigate(location.pathname, { replace: true, state: {} });
        window.dispatchEvent(new Event("resize"));
      }
    }, 100);
    return () => clearInterval(waitForDom);
  }, [location.state, dispatch, navigate, location.pathname]);

  useEffect(() => {
    if (schoolId) {
      if (!teachers?.length) {
        dispatch(fetchTeachers(schoolId));
      }
      if (!periods?.length) {
        dispatch(fetchPeriods(schoolId));
      }
    }
  }, [dispatch, schoolId]); // Only trigger when schoolId is available

  const handleTeacherChange = (e) => {
    const teacherId = e.target.value;
    setSelectedTeacher(teacherId);

    if (teacherId && schoolId) {
      dispatch(fetchTeacherTimetable(teacherId));
      if (run && activeTour === "teacherTimetable") {
        dispatch(stopTour());
        navigate("/timetable/substitute", {
          state: { startSubstituteTeacherTour: true },
        });
      }
    }
  };

  // Filter teachers based on user role
  const filteredTeachers = roles === "user" ? teachers.filter(teacher => teacher.user_id === userId) : teachers;

  // Auto-select the first teacher for "user" role
  useEffect(() => {
    if (roles === "user" && filteredTeachers.length > 0 && !selectedTeacher) {
      setSelectedTeacher(filteredTeachers[0].teacher_id);
      dispatch(fetchTeacherTimetable(filteredTeachers[0].teacher_id));
    }
  }, [filteredTeachers, roles, selectedTeacher, dispatch]);

  if (!schoolId) {
    return (
      <div className="p-4">
        <Alert variant="warning">
          Please log in as a user associated with a school to view teacher
          timetables.
        </Alert>
      </div>
    );
  }

  if (teachersLoading || periodsLoading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading data...</p>
      </div>
    );
  }

  if (teachersError) {
    return (
      <Alert variant="danger">Error loading teachers: {teachersError}</Alert>
    );
  }

  if (periodsError) {
    return (
      <Alert variant="danger">Error loading periods: {periodsError}</Alert>
    );
  }

  return (
    <>
      {/* <GlobalJoyrideCloseButton /> */}
      <div className="px-2">
        <div className="py-3 px-5">
          {/* <h2 className="text-primary fw-bold mb-0"> View Teacher Timetable</h2> */}
          <Form className="mb-4">
       
              <Form.Group className="mb-3">
                <Form.Label>Select Teacher</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedTeacher}
                  className="teacher-select-step"
                  onChange={handleTeacherChange}
                  disabled={teachersLoading} // Disable while teachers are loading
                >
                  <option value="">Select a teacher</option>
                  {filteredTeachers.length > 0 &&
                    filteredTeachers?.map((teacher) => (
                      <option key={teacher.teacher_id} value={teacher.teacher_id}>
                        {teacher.name}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>
      
          </Form>

          {timetableLoading && selectedTeacher && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="info" />
              <p className="mt-2">Loading teacher timetable...</p>
            </div>
          )}

          {timetableError && selectedTeacher && (
            <Alert variant="danger">
              Error loading timetable: {timetableError}
            </Alert>
          )}

          {!selectedTeacher && (
            <Alert variant="info">
              Please select a teacher from the dropdown to view their timetable.
            </Alert>
          )}

          {selectedTeacher &&
            teacherTimetable?.length === 0 &&
            !timetableLoading &&
            !timetableError && (
              <Alert variant="secondary">
               {roles === "user"
                  ? "You have no timetable data available."
                  : "No timetable data available for the selected teacher."}
              </Alert>
            )}

          {selectedTeacher &&
            teacherTimetable?.length > 0 &&
            !timetableLoading && (
              <TeacherTimeTableView
                data={teacherTimetable}
                allPeriods={periods}
              />
            )}
        </div>
      </div>
    </>
  );
};

export default ViewTeacherTimeTableManagement;
